import { CustomThemedResource } from "@/comps/image"
import {
	ErrorMessage,
	SuccessMessage,
} from "@/comps/message"
import { PlayWithProvider, Timeline } from "@/comps/player"
import { assetUrl } from "@/utils/cdn"
import { NotificationContext } from "@/utils/notification"
import { useShortCut } from "@kartyom/shared-components"
import axios from "axios"
import clsx from "clsx"
import { CompletedMessageProps } from "controllers/dashboard"
import { useTranslation } from "next-i18next"
import QRCode from "qrcode.react"
import {
	useContext,
	useEffect,
	useRef,
	useState,
} from "react"
import { SocialButton } from "sections/gallery/ad-examples-popup"
import { socials } from "sections/history/generations"
import { VideoProvider } from "video-provider"

export function CompletedMessagePopup(props: {
	initial: CompletedMessageProps
	setCompletedMessage: (
		v: CompletedMessageProps | null,
	) => void
}) {
	const { initial, setCompletedMessage } = props

	const {
		videoUrl,
		videoWidth,
		videoHeight,
		previewImage,
	} = initial
	const videoRef = useRef<HTMLVideoElement>(null)
	const [qrPopup, setQrPopup] = useState(false)

	useShortCut({
		shortCut: "Escape",
		handler: () => {
			setCompletedMessage(null)
			setQrPopup(false)
		},
	})

	return (
		<div
			id="completed-message-popup"
			onClick={(e) => {
				const target = e.target
				if (target instanceof HTMLElement) {
					if (target.id === "completed-message-popup") {
						setCompletedMessage(null)
					}
					if (
						!target.className.includes("chat-qr") &&
						qrPopup
					) {
						setQrPopup(false)
					}
				}
			}}
			className={clsx(
				"absolute inset-0 z-[110] h-screen w-full bg-color-popup",
				"no-scrollbar tablet:fixed tablet:overflow-y-scroll tablet:py-[100px]",
				"tablet:flex tablet:items-center tablet:justify-center",
			)}>
			<div
				className={clsx(
					"no-scrollbar relative z-[101] flex h-full w-full flex-col",
					"tablet:w-[669px] tablet:rounded-[12px] desktop:flex-row",
					"tablet:rounded-[12px] desktop:w-auto",
				)}>
				<button
					onClick={() => setCompletedMessage(null)}
					className={clsx(
						"absolute rounded-full bg-color-popup p-[7px]",
						"left-[20px] top-[20px] z-[111] tablet:right-[-40px]",
						"tablet:left-auto tablet:top-0 tablet:bg-color-popup-cell",
						"tablet:border tablet:border-color-separator",
					)}>
					<img
						src={assetUrl("/general/close-dark.svg")}
						alt="close icon"
						className="h-[18px] w-[18px] desktop:hidden"
					/>
					<CustomThemedResource
						source="/general/close"
						format="svg"
						alt="close icon"
						className="hidden h-[18px] w-[18px] desktop:block"
					/>
				</button>
				<div
					className={clsx(
						"relative flex max-h-[calc(100svh-88px)] w-full shrink-0 overflow-hidden bg-color-dashboard-bg",
						"tablet:h-[667px] tablet:max-h-none tablet:items-center tablet:justify-center tablet:rounded-t-[10px] tablet:bg-[transparent]",
						"desktop:h-[680px] desktop:w-[680px] desktop:flex-1 desktop:rounded-l-[10px] desktop:rounded-tr-none",
					)}>
					<div
						className={clsx(
							"absolute inset-0 h-full w-full overflow-hidden bg-color-black filter tablet:h-auto",
							"hidden overflow-hidden tablet:block desktop:rounded-r-none",
						)}>
						<img
							src={previewImage}
							alt="preview image"
							className="h-full w-full scale-110 object-cover opacity-30 blur-lg"
						/>
					</div>

					{videoUrl && (
						<div
							style={{
								aspectRatio: videoWidth / videoHeight,
							}}
							className="relative z-10 mx-auto h-full bg-color-cell tablet:mx-0 tablet:h-auto tablet:max-h-[600px] tablet:w-auto tablet:max-w-[500px] tablet:rounded-t-[10px] desktop:max-h-[600px] desktop:max-w-[525px] desktop:rounded-[10px]">
							<video
								style={{
									aspectRatio: videoWidth / videoHeight,
								}}
								src={videoUrl}
								poster={previewImage}
								ref={videoRef}
								playsInline
								autoPlay
								loop
								className="relative z-10 tablet:max-h-[600px] tablet:w-auto tablet:max-w-[500px] tablet:rounded-t-[10px] desktop:max-h-[600px] desktop:max-w-[525px] desktop:rounded-[10px]"
							/>
							<VideoProvider
								remember={[]}
								element={videoRef}>
								<div className="absolute bottom-0 left-0 z-50 w-full text-color-white">
									<Timeline radius="0px 0px 10px 10px" />
								</div>
								<PlayWithProvider />
							</VideoProvider>
						</div>
					)}
				</div>

				<CompletedContentSection
					initial={initial}
					qrPopup={qrPopup}
					setQrPopup={setQrPopup}
				/>
			</div>
		</div>
	)
}

function CompletedContentSection(props: {
	initial: CompletedMessageProps
	qrPopup: boolean
	setQrPopup: (v: boolean) => void
}) {
	const {
		title,
		videoDesc,
		shareUrl,
		videoUrl,
		previewImage,
		resultId,
	} = props.initial
	const { setQrPopup, qrPopup } = props
	const [showMore, setShowMore] = useState(false)
	const { t } = useTranslation()

	const [downloadApi, setDownloadApi] = useState(false)

	useEffect(() => {
		if (downloadApi) return
		const isIOS = /iPad|iPhone|iPod/.test(
			navigator.userAgent,
		)
		const isSafari = /^((?!chrome|android).)*safari/i.test(
			navigator.userAgent,
		)

		if (isIOS || isSafari) {
			setDownloadApi(true)
			return
		}
	}, [downloadApi])

	return (
		<div
			className={clsx(
				"flex flex-1 flex-col bg-color-popup-cell tablet:flex-none tablet:flex-col-reverse tablet:bg-[unset] desktop:w-[318px] desktop:bg-color-popup-cell",
				"desktop:h-[680px] desktop:rounded-r-[12px]",
				"pb-[38px] tablet:pb-0 desktop:relative desktop:justify-between",
			)}>
			<div
				className={clsx(
					"flex w-full items-center gap-[12px] py-[20px]",
					"bg-color-background tablet:flex-col tablet:bg-color-popup-cell",
					"tablet:mb-[100px] tablet:rounded-b-[12px] desktop:rounded-bl-none",
					"px-[16px] desktop:mb-0 desktop:rounded-br-[12px]",
					"fixed bottom-0 left-0 z-50 tablet:relative",
				)}>
				<button
					onClick={() => setQrPopup(true)}
					className={clsx(
						"w-full items-center bg-color-chat-alpha desktop:flex",
						"hidden h-[48px] justify-center rounded-[4px]",
					)}>
					<div className="flex items-center gap-[4px]">
						<CustomThemedResource
							source="/general/edit"
							className="h-[18px] w-[18px]"
							format="png?aaa"
							alt="edit icon"
						/>
						<span className="text-[15px] font-600 text-blue-700">
							{t("lbl_edit")}
						</span>
					</div>
				</button>
				<a
					href={shareUrl}
					className={clsx(
						"flex w-full items-center bg-color-chat-alpha",
						"h-[48px] justify-center rounded-[4px] desktop:hidden",
					)}>
					<div className="flex items-center gap-[4px]">
						<CustomThemedResource
							source="/general/edit"
							className="h-[18px] w-[18px]"
							format="png?aaa"
							alt="edit icon"
						/>
						<span className="text-[15px] font-600 text-blue-800">
							{t("lbl_edit")}
						</span>
					</div>
				</a>
				<a
					href={
						videoUrl === ""
							? `/api/download-music?link=${encodeURIComponent(
									previewImage,
							  )}`
							: downloadApi
							? `/api/download-video?link=${encodeURIComponent(
									videoUrl,
							  )}&name=${encodeURIComponent(
									props.initial.title,
							  )}`
							: videoUrl
					}
					onClick={() =>
						axios.post("/api/wizard-save-result-event", {
							result_id: resultId,
						})
					}
					datatype="mp4"
					download={props.initial.title + ".mp4"}
					target="_blank"
					rel="noreferrer noopener"
					className={clsx(
						"flex w-full items-center bg-color-chat",
						"h-[48px] justify-center rounded-[4px]",
					)}>
					<div className="flex items-center gap-[4px]">
						<img
							src={assetUrl("/general/download-white.png")}
							className="h-[18px] w-[18px]"
							alt="edit icon"
						/>
						<span className="text-[15px] font-600 text-color-white">
							{t("lbl_download")}
						</span>
					</div>
				</a>
			</div>

			<div
				className={clsx(
					"flex flex-col gap-[10px] bg-color-popup-cell pb-[50px]",
					" pt-[10px] tablet:pb-0 desktop:rounded-tr-[12px]",
				)}>
				<span className="px-[16px] text-[18px] font-500 text-blue-800">
					{title}
				</span>
				<p
					className={clsx(
						showMore && "max-h-[100px] overflow-y-scroll",
						"no-scrollbar px-[16px] text-[16px] font-400 text-blue-800",
						"tablet:max-h-[200px] desktop:max-h-[300px]",
					)}>
					{showMore
						? videoDesc
						: videoDesc.substring(0, 150)}
					<span
						onClick={() => setShowMore((old) => !old)}
						className={clsx(
							videoDesc.length <= 150 && "hidden",
							showMore && "ml-[10px]",
							"cursor-pointer text-[16px] font-600 text-blue-800",
						)}>
						{showMore ? "less" : "...more"}
					</span>
				</p>

				<SocialShareSection shareUrl={shareUrl} />
			</div>

			<div
				className={clsx(
					qrPopup ? "desktop:flex" : "desktop:hidden",
					"absolute hidden h-full w-full pb-[150px]",
					"items-end justify-center rounded-r-[12px]",
				)}>
				<div
					className={clsx(
						"h-[300px] w-[220px] bg-blue-cell shadow-xl",
						"flex flex-col items-center rounded-[10px]",
						"chat-qr justify-center gap-[22px] border",
						"border-color-separator",
					)}>
					<div className="chat-qr rounded-[8px] bg-color-white p-[14px]">
						<QRCode
							className="chat-qr"
							size={135}
							bgColor="#fff"
							fgColor="#575a6b"
							value={shareUrl}
							renderAs={"canvas"}
						/>
					</div>

					<div className="chat-qr flex flex-col items-center gap-[13px]">
						<span className="chat-qr text-[18px] font-600 text-blue-700">
							{t("txt_chat_qr_title")}
						</span>
						<p className="chat-qr w-[197px] text-center text-[14px] font-400 text-blue-700">
							{t("txt_chat_qr_desc")}
						</p>
					</div>
				</div>
			</div>
		</div>
	)
}

export function SocialShareSection(props: {
	shareUrl: string
	isSingle?: boolean
}) {
	const { shareUrl, isSingle } = props
	const { t } = useTranslation()
	const { notify } = useContext(NotificationContext)

	async function handleCopy(text: string) {
		try {
			await navigator.clipboard.writeText(text)
			notify(
				<SuccessMessage>
					{t("common:txt_successfully_copied")}
				</SuccessMessage>,
			)
		} catch (e) {
			console.error(e)
			notify(
				<ErrorMessage>
					{t("common:txt_couldnt_copy")}
				</ErrorMessage>,
			)
		}
	}

	return (
		<div
			className={clsx(
				isSingle && "bg-color-cell tablet:rounded-[8px]",
				isSingle && "tablet:border-none",
				"border-y  border-y-color-separator",
				"flex flex-col gap-[12px] py-[16px]",
			)}>
			<span
				className={clsx(
					isSingle && "tablet:hidden",
					"px-[16px] text-[18px] font-500 text-blue-700",
				)}>
				{t("txt_share_to")}
			</span>
			<div
				className={clsx(
					isSingle &&
						"tablet:overflow-x-hidden tablet:pl-[30px] tablet:pr-[16px]",
					"flex w-full overflow-x-scroll px-[16px]",
					"no-scrollbar -mb-[30px] gap-[8px] pb-[30px]",
				)}>
				<button
					onClick={async (e) => {
						const target = e.currentTarget
						target.disabled = true
						setTimeout(() => {
							target.disabled = false
						}, 3000)
						await handleCopy(shareUrl)
					}}
					className={clsx(
						"flex items-center justify-center",
						"transition-colors hover:bg-blue-300",
						"h-[44px] w-[44px] shrink-0 rounded-full bg-color-surface",
					)}>
					<img
						src={assetUrl(
							"/ai-tools/history/copy-primary.svg",
						)}
						alt="copy icon"
						className="h-[20px] w-[20px]"
					/>
				</button>
				{socials.map((props) => (
					<SocialButton
						{...props}
						slug={shareUrl}
						key={props.icon ?? props.light}
					/>
				))}
			</div>
		</div>
	)
}
